import type { JobCard } from "~/interface/Job"
import { JobAction } from "~/interface/Job"

const LOCAL_STORAGE_PREFIX = "openmenu-dynamodb"

const buildPayloads = (job: JobCard, customerId: string, businessDate: string): any[] => {
  const items: any[] = Array.isArray((job as any).__items) ? (job as any).__items : []
  const openMenuItems = items.filter((it) => it?.isOpenMenu === true)
  if (openMenuItems.length === 0) return []

  const orderId = job.alias || job.jobId || ""
  // The receipt this PAY card produced (pos.payment.ts createJobchain). Stamped on
  // every row so the server can mark them voided when the bill is voided — the row
  // timestamp is a different clock read from the receipt Date and can't be matched.
  const receiptNonce = (job as any).receiptNonce || ""

  return openMenuItems.map((it: any, idx: number) => {
    const price = Number(it.price ?? 0)
    const quantity = Number(it.quantity ?? 1)
    const baseTs = job.timestamp ? new Date(job.timestamp as any).getTime() : Date.now()
    const timestamp = new Date(baseTs + idx).toISOString()
    return {
      customerId,
      timestamp,
      name: it.name || "",
      // group: it.name || "",
      openMenuName: it.openMenuName || "",
      orderId,
      receiptNonce,
      businessDate,
      price,
      quantity,
      extendedPrice: price * quantity,
      discountedPrice: price * quantity,
    }
  })
}

const saveLocalStorage = (key: string, payload: any) => {
  if (!process.client) return
  try {
    localStorage.setItem(`${LOCAL_STORAGE_PREFIX}:${key}`, JSON.stringify(payload))
  } catch (error) {
    console.error("openMenu: failed to save local", error)
  }
}

const removeLocalStorage = (key: string) => {
  if (!process.client) return
  try {
    localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}:${key}`)
  } catch (error) {
    console.error("openMenu: failed to remove local", error)
  }
}

const getPending = (): { key: string; payload: any }[] => {
  if (!process.client) return []
  const out: { key: string; payload: any }[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(`${LOCAL_STORAGE_PREFIX}:`)) {
        const raw = localStorage.getItem(k)
        if (raw) out.push({ key: k.slice(LOCAL_STORAGE_PREFIX.length + 1), payload: JSON.parse(raw) })
      }
    }
  } catch (error) {
    console.error("openMenu: failed to read pending", error)
  }
  return out
}

const postOne = async (dataservice: ReturnType<typeof useDataservice>, key: string, payload: any) => {
  try {
    await dataservice.openMenu.post(payload)
    removeLocalStorage(key)
  } catch (error) {
    console.error("openMenu: post failed, kept in local storage", error)
  }
}

export const syncPendingOpenMenu = async (dataservice: ReturnType<typeof useDataservice>) => {
  const pending = getPending()
  if (!pending.length) return
  const batchSize = 25
  for (let i = 0; i < pending.length; i += batchSize) {
    const batch = pending.slice(i, i + batchSize)
    await Promise.all(batch.map(({ key, payload }) => postOne(dataservice, key, payload)))
  }
}

export default defineNuxtPlugin(({ $pinia }) => {
  if (!process.client) return

  const jobV1Store = usePosJobsStore()
  const dataservice = useDataservice()
  const customerId = dataservice.customerId

  onDataserviceReady(dataservice, () => void syncPendingOpenMenu(dataservice))
  window.addEventListener("online", () => {
    if (dataservice.isInited.value) void syncPendingOpenMenu(dataservice)
  })

  jobV1Store.$onAction((action) => {
    if (action.name !== "add") return
    const job = action.args[0] as JobCard
    const isSync = action.args[1]
    if (!isSync) return
    if (job?.type !== JobAction.PAY) return

    action.after(() => {
      const businessDate = jobV1Store.getBusinessDateFromTimestamp(job.timestamp as any)
      const payloads = buildPayloads(job, customerId.value, businessDate)
      payloads.forEach((p) => {
        const key = `${p.timestamp}__${p.name}`
        saveLocalStorage(key, p)
        void postOne(dataservice, key, p)
      })
    })
  })
})
